/**
 * Admin users API client.
 *
 * Mirrors /admin/users on tacosai-admin-api. The two write paths this UI
 * adds — change-handle + set-publish-cap — are audited server-side.
 * `id` is the `u_<n>` form the API returns; the endpoint strips the prefix.
 */
import { request } from './client.js';

export type UserRole = 'owner' | 'system_admin' | 'creator';

export interface AdminUser {
  id: string;
  username: string;
  email: string | null;
  full_name: string | null;
  role: UserRole;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
  ftp_publish_cap: number;
}

export interface ListUsersQuery {
  role?: UserRole;
  is_active?: 'true' | 'false';
  q?: string;
}

export async function listUsers(query: ListUsersQuery = {}): Promise<{ items: AdminUser[] }> {
  return request<{ items: AdminUser[] }>({
    method: 'GET',
    url: '/admin/users',
    query: query as Record<string, string | undefined>,
  });
}

/** Change the public handle. 409 `username_taken` if the (case-insensitive) handle is in use. */
export async function changeUsername(
  id: string,
  body: { username: string; reason: string },
): Promise<AdminUser> {
  return request<AdminUser>({ method: 'PUT', url: `/admin/users/${id}/username`, body });
}

/** Raise/lower the funding concurrent-publish cap (default 3). */
export async function setPublishCap(
  id: string,
  body: { ftp_publish_cap: number; reason: string },
): Promise<AdminUser> {
  return request<AdminUser>({ method: 'PUT', url: `/admin/users/${id}/ftp-publish-cap`, body });
}

export async function suspendUser(id: string, body: { reason: string }): Promise<AdminUser> {
  return request<AdminUser>({ method: 'POST', url: `/admin/users/${id}/suspend`, body });
}

export async function activateUser(id: string, body: { reason: string }): Promise<AdminUser> {
  return request<AdminUser>({ method: 'POST', url: `/admin/users/${id}/activate`, body });
}

/**
 * Admin-set a temporary password. The server mints an argon2id hash; the
 * plaintext is sent over TLS and never stored or logged. The user should
 * change it after first login.
 */
export async function setUserPassword(
  id: string,
  body: { password: string; reason: string },
): Promise<AdminUser> {
  return request<AdminUser>({ method: 'POST', url: `/admin/users/${id}/set-password`, body });
}
